import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';

import { useGetTeachers } from '../../hooks/teachers/useGetTeachers';
import { useDeleteTeacher } from '../../hooks/teachers/useDeleteTeacher';
import { useDeleteAllTeachers } from '../../hooks/teachers/useDeleteAllTeachers';
import { useForceDeleteTeacher } from '../../hooks/teachers/useForceDeleteTeacher';
import { useForceDeleteAllTeachers } from '../../hooks/teachers/useForceDeleteAllTeachers';
import { useRestoreTeacher } from '../../hooks/teachers/useRestoreTeacher';
import { useRestoreAllTeachers } from '../../hooks/teachers/useRestoreAllTeachers';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import TeachersTableColumns from './Table/TeachersTableColumns';
import TeachersTableRow from './Table/TeachersTableRow';
import ModelShowTeacher from './ModelShowTeacher';

const TeachersTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const [filterSchoolId, setFilterSchoolId] = useState('');
    const [openView, setOpenView] = useState(false);
    const [viewId, setViewId] = useState(null);

    const perms = useMemo(() => ({
        list: has('teachers_list'), create: has('teachers_create'), edit: has('teachers_edit'),
        show: has('teachers_show'), delete: has('teachers_delete'),
        archive_list: has('teachers_archive_list'), restore: has('teachers_restore'), force_delete: has('teachers_force_delete'),
    }), [has]);

    const { deleteTeacher, isDelete } = useDeleteTeacher();
    const { deleteAllTeachers, isDelete: isDeletingAll } = useDeleteAllTeachers();
    const { forceDeleteTeacher } = useForceDeleteTeacher();
    const { forceDeleteAllTeachers } = useForceDeleteAllTeachers();
    const { restoreTeacher } = useRestoreTeacher();
    const { restoreAllTeachers } = useRestoreAllTeachers();
    const { schools } = useGetSchools(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, selectedIds, menuAnchorEl, selectedItem,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/teachers', deleteSingle: deleteTeacher, deleteAll: deleteAllTeachers,
        forceDeleteSingle: forceDeleteTeacher, forceDeleteAll: forceDeleteAllTeachers,
        restoreSingle: restoreTeacher, restoreAll: restoreAllTeachers, isArchive,
    });

    const handleView = () => { if (selectedItem) { setViewId(selectedItem.id); setOpenView(true); } handleMenuClose(); };

    const filters = useMemo(() => ({
        search: search || undefined, sort_by: sortBy || undefined, sort_dir: sortDir || undefined,
        school_id: filterSchoolId || undefined, ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterSchoolId, isArchive]);

    const { isLoading, error, teachers, meta } = useGetTeachers(page, perPage, filters);

    const renderRow = (params) => (
        <TeachersTableRow key={params.key || params.item.id} item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark} page={params.page} perPage={params.perPage}
            order={params.order} totalItems={teachers.length} selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick} onMenuOpen={params.onMenuOpen} />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" value={filterSchoolId}
                    onChange={(e) => setFilterSchoolId(e.target.value)}
                    label={isArabic ? 'فلتر حسب المدرسة' : 'Filter by School'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{ minWidth: 220, ...(isArabic && {
                        '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                        '& .MuiInputLabel-shrink': { right: 32 },
                        '& .MuiSelect-icon': { left: 7, right: 'unset' },
                        '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                        '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                    }) }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                    <MenuItem value="">{isArabic ? 'الكل' : 'All'}</MenuItem>
                    {(schools || []).map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
                </TextField>
                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && <ArchiveButton to="/teachers/archive" />}
                    {isArchive && perms.list && <ArchiveButton to="/teachers" archiveLabelAr="العودة للقائمة" archiveLabelEn="Back to List" />}
                </Box>
            </Box>

            <TableLayout
                title={isArabic ? (isArchive ? 'أرشيف المعلمين' : 'المعلمون') : (isArchive ? 'Teachers Archive' : 'Teachers')}
                subtitle={isArabic ? (isArchive ? 'إدارة المعلمين المحذوفين' : 'إدارة المعلمين') : (isArchive ? 'Manage deleted teachers' : 'Manage teachers')}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة معلم' : 'Add Teacher'}
                onAddClick={() => navigate('/teachers/create')} addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading} error={error} items={teachers} meta={meta}
                page={page} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds} onSelectAllClick={(e) => handleSelectAllClick(e, teachers)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete} onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={TeachersTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuView={perms.show ? handleView : undefined}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive ? (perms.force_delete ? handleMenuDelete : undefined) : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit} menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا يوجد معلمون في الأرشيف' : 'لا يوجد معلمون'}
                emptyLabelEn={isArchive ? 'No archived teachers' : 'No teachers'}
                loadErrorAr="حدث خطأ أثناء تحميل المعلمين" loadErrorEn="Failed to load teachers"
            />
            {openView && <ModelShowTeacher teacherId={viewId} open={openView} onClose={() => setOpenView(false)} />}
        </>
    );
};

export default TeachersTable;
